// "Using an agent?" affordance for the library: a quiet pill that opens a
// panel holding a ready-to-paste prompt teaching any agent how to search
// The Vibeary (MCP first, JSON API as the fallback).

import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Check, Copy, Info } from "@/components/icons";
import { renderAgentPrompt } from "@/lib/agent-surface";

export function AgentPromptHint({ total }: { total: number }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const panelId = useId();

  const prompt = renderAgentPrompt(total > 0 ? { total, categories: [] } : null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    // No autofocus: focusing the textarea would scroll the grid into view.
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      fieldRef.current?.select();
      document.execCommand?.("copy");
    }
    setCopied(true);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className="group inline-flex min-h-11 items-center gap-2 rounded-full py-1.5 pl-2 pr-4 text-sm text-muted-foreground ring-1 ring-border/60 transition-[color,background-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-foreground hover:bg-muted/40 hover:ring-foreground/20 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          aria-hidden
          className="grid h-7 w-7 place-items-center rounded-full bg-muted text-foreground transition-colors duration-300 group-hover:bg-foreground group-hover:text-background"
        >
          <Info className="h-3.5 w-3.5" />
        </span>
        Trying to access this with your agent?
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            id={panelId}
            initial={{ opacity: 0, y: -6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -4, filter: "blur(2px)" }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="absolute right-0 z-[70] mt-3 w-[min(92vw,34rem)] rounded-3xl bg-card p-5 ring-1 ring-border/70 shadow-[0_1px_2px_rgba(20,20,40,0.06),0_18px_40px_-24px_rgba(20,20,40,0.35)] max-md:fixed max-md:inset-x-4 max-md:right-auto max-md:w-auto"
          >
            <p className="text-sm leading-relaxed text-muted-foreground">
              Paste this into Claude, Cursor, ChatGPT or any MCP client. Your
              agent can then search every gem live — no key, no signup.
            </p>

            <textarea
              ref={fieldRef}
              readOnly
              value={prompt}
              rows={8}
              spellCheck={false}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Prompt for your agent"
              className="mt-4 block max-h-64 w-full resize-none overflow-auto rounded-2xl bg-muted/60 p-4 font-mono text-[11.5px] leading-relaxed text-foreground/80 ring-1 ring-inset ring-border/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={copy}
                className="inline-flex min-h-11 min-w-[10rem] items-center justify-center gap-2 rounded-full bg-foreground px-5 text-sm font-medium text-background transition-[scale,opacity] duration-200 ease-[cubic-bezier(0.2,0,0,1)] hover:opacity-90 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <AnimatePresence mode="wait" initial={false}>
                  {copied ? (
                    <motion.span
                      key="done"
                      initial={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
                      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
                      transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                      className="inline-flex items-center gap-2"
                    >
                      <Check className="h-4 w-4" aria-hidden />
                      Copied
                    </motion.span>
                  ) : (
                    <motion.span
                      key="copy"
                      initial={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
                      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
                      transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                      className="inline-flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" aria-hidden />
                      Copy prompt
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              <p className="text-xs text-muted-foreground">
                <a
                  href="/for-agents"
                  className="underline-offset-4 hover:text-foreground hover:underline"
                >
                  Full agent docs
                </a>
                {" · "}
                <a
                  href="/library/prompt.txt"
                  className="underline-offset-4 hover:text-foreground hover:underline"
                >
                  prompt.txt
                </a>
                {" · "}
                <a
                  href="/llms.txt"
                  className="underline-offset-4 hover:text-foreground hover:underline"
                >
                  llms.txt
                </a>
              </p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
